import React from 'react';
import { Box } from '@mui/material';

import { SidebarTabNames } from './constants';
import { SidebarTabNamesType } from './types';
import GeneralTab from './SidebarTabs/GeneralTab';
import OngoingCallsTab from './SidebarTabs/OngoingCallsTab';
import CallAnalysisTab from './SidebarTabs/CallAnalysisTab';
import AgentTab from './SidebarTabs/AgentTab';
import PhoneNumbersTab from './SidebarTabs/PhoneNumbersTab';
import AnalysisSummaryTab from './SidebarTabs/analysis/CallAnalysisStatistics';
import SettingsTab from './SidebarTabs/PostCallTab';
import SipTrunkTab from './SidebarTabs/SipTrunkTab';
import SquadsTab from './SidebarTabs/SquadsTab';
import { CampaignDto } from '@shared/services/apiService/apiService';
import { CampaignTypeEnum } from '@shared/constants/campaign.constants';
import { AmbienceSettingsForm, AmbienceSettingsDraft } from '@features/Admin/AmbienceSettings';
import { AgentDto, BackgroundSoundResponseDto } from '@shared/services/apiService/apiService';

interface EditCampaignTabContentProps {
  activeTab: SidebarTabNamesType;
  campaignData?: CampaignDto;
  campaignId?: string;
  isLoading: boolean;
  isLoadingCampaignAgents: boolean;
  isSquadCampaign: boolean;
  isCompanyFetched: boolean;
  agents: AgentDto[];
  onAgentCreated: () => Promise<void>;
  refetch: () => Promise<unknown>;
  refetchCampaignAgents: () => Promise<unknown>;
  responseLoadingFlag: boolean;
  setLeadListChanged: (changed: boolean) => void;
  isLoadingBackgroundSounds: boolean;
  backgroundSounds: BackgroundSoundResponseDto[];
  ambienceSettingsDraft: AmbienceSettingsDraft | null;
  setAmbienceSettingsDraft: (draft: AmbienceSettingsDraft) => void;
  isAmbienceSettingsReadOnly: boolean;
  hasAttemptedAmbienceSettingsSave: boolean;
}

export const EditCampaignTabContent: React.FC<EditCampaignTabContentProps> = ({
  activeTab,
  campaignData,
  campaignId,
  isLoading,
  isLoadingCampaignAgents,
  isSquadCampaign,
  isCompanyFetched,
  agents,
  onAgentCreated,
  refetch,
  refetchCampaignAgents,
  responseLoadingFlag,
  setLeadListChanged,
  isLoadingBackgroundSounds,
  backgroundSounds,
  ambienceSettingsDraft,
  setAmbienceSettingsDraft,
  isAmbienceSettingsReadOnly,
  hasAttemptedAmbienceSettingsSave,
}) => {
  switch (activeTab) {
    case SidebarTabNames.GENERAL:
      return <GeneralTab campaign={campaignData} />;
    case SidebarTabNames.AGENT:
      return (
        <AgentTab
          isLoading={isLoading || isLoadingCampaignAgents}
          onAgentCreated={onAgentCreated}
          refetch={async () => {
            await refetch();
            if (!isSquadCampaign) {
              await refetchCampaignAgents();
            }
          }}
          agents={agents}
        />
      );
    case SidebarTabNames.SQUADS:
      return (
        <SquadsTab
          isLoading={isLoading || isLoadingCampaignAgents}
          campaign={campaignData}
          agents={agents}
        />
      );
    case SidebarTabNames.PHONE_NUMBERS:
      return campaignData?.type === CampaignTypeEnum.OUTBOUND ? (
        <PhoneNumbersTab
          campaign={campaignData}
          refetch={refetch}
          responseLoadingFlag={responseLoadingFlag}
          setLeadListChanged={setLeadListChanged}
        />
      ) : null;
    case 'analysisSummary':
      return campaignData ? <AnalysisSummaryTab campaign={campaignData} /> : null;
    case SidebarTabNames.SETTINGS:
      return campaignData ? (
        <SettingsTab campaignId={campaignData.id} isCampaignActive={campaignData.status} />
      ) : null;
    case SidebarTabNames.SIP_TRUNK:
      return isCompanyFetched && campaignData ? <SipTrunkTab campaign={campaignData} /> : null;
    case SidebarTabNames.AMBIENCE_SETTINGS:
      return campaignData ? (
        <Box sx={{ width: '760px', maxWidth: '100%' }}>
          <AmbienceSettingsForm
            isLoadingSounds={isLoadingBackgroundSounds}
            sounds={backgroundSounds}
            value={ambienceSettingsDraft}
            onChange={setAmbienceSettingsDraft}
            ambientDisabled={isAmbienceSettingsReadOnly}
            thinkingDisabled={isAmbienceSettingsReadOnly}
            showOverrideControls
            hasAttemptedSave={hasAttemptedAmbienceSettingsSave}
          />
        </Box>
      ) : null;
    case SidebarTabNames.ONGOING_CALLS:
      return campaignData && campaignId ? (
        <OngoingCallsTab
          campaignId={campaignId}
          campaignData={campaignData}
          campaignType={campaignData.type}
        />
      ) : null;
    case SidebarTabNames.CALL_ANALYSIS:
      return campaignData ? <CallAnalysisTab campaign={campaignData} /> : null;
    case SidebarTabNames.STATISTICS:
      return campaignData ? <AnalysisSummaryTab campaign={campaignData} /> : null;
    default:
      return null;
  }
};
